"use client"

import type { BackendAbTestAssignment } from "@/shared/types/global-store"
import {
  getSmqVariantOverride,
  storeSmqVariantOverride,
  type SmqVariant,
} from "@/shared/utils/storage"
import { newSubscriptionFlowService } from "@/store/machine"
import { useActor } from "@xstate/react"
import { useEffect, useState } from "react"

/**
 * Backend AB test driving the "Send my quote" entry point (DLM-1958). The lead
 * API assigns every new DE health lead an `ab_test` entry keyed by test type
 * (DLM-1889); the LEAD_CREATION slot carries this test for as long as it runs.
 * The entry's `name` must match the config rows Julien created in the ab-test
 * table on 2026-09-30 (one per DE health carrier, Seyna and Axeria): a
 * LEAD_CREATION test with another name must never toggle the SMQ UI.
 */
export const SMQ_AB_TEST_TYPE = "LEAD_CREATION"
export const SMQ_AB_TEST_NAME = "ab-test-smq-entry-point"

const SMQ_AB_TEST_BRANCHES: Partial<Record<BackendAbTestAssignment["branch"], SmqVariant>> = {
  A: "v1",
  B: "v2",
}

/**
 * Legacy assignment (DLM-1769): before the `ab_test` field existed, the same
 * test split leads across two identical pricing versions (v14.0.1 = A,
 * v14.0.2 = B; "va" = annual-payment twin). Leads created then keep their
 * pricing version and never receive an `ab_test` entry — the backend only
 * assigns when the quote has no pricing version yet — so this mapping stays
 * until v14.0.x leads have left the funnel.
 */
const LEGACY_SMQ_AB_PRICING_VERSIONS: Record<string, SmqVariant> = {
  "v14.0.1": "v1",
  "va14.0.1": "v1",
  "v14.0.2": "v2",
  "va14.0.2": "v2",
}

export function getSmqVariantFromAbTest(
  abTest: Record<string, BackendAbTestAssignment> | undefined
): SmqVariant | undefined {
  const assignment = abTest?.[SMQ_AB_TEST_TYPE]
  if (!assignment || assignment.name !== SMQ_AB_TEST_NAME) {
    return undefined
  }
  return SMQ_AB_TEST_BRANCHES[assignment.branch]
}

/**
 * Resolves which "Send my quote" entry point to render, by priority:
 * 1. QA override — ?smq_variant=v2 (or v1), persisted in sessionStorage so it
 *    survives in-funnel navigation and can force a variant over the backend
 *    assignment when debugging;
 * 2. backend assignment carried by `quote.meta.ab_test` (NOT_ASSIGNED, e.g. a
 *    lead without a pet name, falls through);
 * 3. legacy assignment carried by the quote's pricing version;
 * 4. "v1" (control).
 *
 * Reads window.location instead of useSearchParams to avoid the App Router
 * Suspense requirement; resolving the override after mount keeps hydration
 * safe.
 */
export function useSmqVariant(): SmqVariant {
  const [state] = useActor(newSubscriptionFlowService)
  const [override, setOverride] = useState<SmqVariant | null>(null)

  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get("smq_variant")
    if (param === "v1" || param === "v2") {
      storeSmqVariantOverride(param)
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync the override from the URL/sessionStorage on mount; resolving after hydration is the intended pattern here (same as the Navbar promo-code sync)
      setOverride(param)
      return
    }
    setOverride(getSmqVariantOverride())
  }, [])

  const assigned = getSmqVariantFromAbTest(state.context.quote?.meta?.ab_test)

  // The machine tracks the version in two places depending on the event that
  // carried it (creation vs retrieval), top-level first.
  const pricingVersion = state.context.pricing_version ?? state.context.quote?.meta?.pricing_version
  const legacy = pricingVersion ? LEGACY_SMQ_AB_PRICING_VERSIONS[pricingVersion] : undefined

  return override ?? assigned ?? legacy ?? "v1"
}
